import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProjectZonesService } from '../services/zones.service';
import type { ProjectZone, ZoneFilters } from '../types/project-zones.types';
import { useState } from 'react';
import { toast } from 'sonner';

export function useProjectZones(companyId: string, initialFilters: ZoneFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ZoneFilters>({
    status: 'all',
    type: 'all',
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['project-zones', companyId, filters],
    queryFn: () => ProjectZonesService.getZones(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['project-zone-stats', companyId, filters.projectId],
    queryFn: () => ProjectZonesService.getZoneStats(companyId, filters.projectId),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<ProjectZone>) => ProjectZonesService.createZone(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-zones'] });
      toast.success('Khởi tạo khu vực thành công');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ProjectZonesService.deleteZone(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-zones'] });
      toast.success('Đã xóa khu vực thành công');
    },
  });

  return {
    zones: query.data?.data || [],
    total: query.data?.total || 0,
    stats: statsQuery.data,
    isLoading: query.isLoading || statsQuery.isLoading,
    filters,
    setFilters,
    createZone: createMutation.mutateAsync,
    deleteZone: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}

export function useProjectZoneDetail(id?: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['project-zones', 'detail', id],
    queryFn: () => ProjectZonesService.getZoneDetail(id!),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (updates: Partial<ProjectZone>) => ProjectZonesService.updateZone(id!, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-zones'] });
      queryClient.invalidateQueries({ queryKey: ['project-zones', 'detail', id] });
      toast.success('Cập nhật khu vực thành công');
    },
  });

  return {
    zone: query.data,
    isLoading: query.isLoading,
    updateZone: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}
